"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Building2, CircleCheck as CheckCircle2, ClipboardCheck, FileCheck, House as Home, Layers, MapPin, Shield, ShieldCheck, Truck, Umbrella, ArrowRight, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const accessContexts = [{
  title: "Period and listed properties",
  description: "Careful anchoring and non-invasive base plates protect historic brickwork, stone cornices, and heritage structures.",
  icon: Home
}, {
  title: "Conservation-sensitive zones",
  description: "Strict compliance with borough regulations, tree preservation orders, and discreet layout planning.",
  icon: Shield
}, {
  title: "Narrow roads and tight mews",
  description: "Coordinated delivery scheduling, pavement licence management, and slimline gantries for constrained lanes.",
  icon: Truck
}, {
  title: "Occupied residences",
  description: "Daily tidy procedures, foam protection on ground-level standards, and safe entry points for residents and visitors.",
  icon: Building2
}, {
  title: "Commercial and retail frontages",
  description: "Pedestrian protection fans, debris netting, and out-of-hours erection to keep shopfronts fully operational.",
  icon: Layers
}, {
  title: "Roof refurbishment and chimneys",
  description: "Purpose-built saddles, chimney stacks, and heavy-duty loading bays designed for slate and tile works.",
  icon: Umbrella
}];
const safetyProtocol = [{
  step: "01",
  name: "Site assessment",
  detail: "Comprehensive ground bearing checks, tie point verification, and risk analysis before a single tube is delivered."
}, {
  step: "02",
  name: "Engineered safe access",
  detail: "TG20 compliant configurations and bespoke design drawings tailored to Richmond's specific structural demands."
}, {
  step: "03",
  name: "Handover inspection",
  detail: "Full sign-off by a qualified supervisor with genuine handover certificates and active Scafftag handover systems."
}, {
  step: "04",
  name: "Statutory 7-day checks",
  detail: "Ongoing weekly safety inspections and post-weather checks logged and provided for client site compliance."
}];
export default function RichmondAccess() {
  const containerRef = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".richmond-header", {
        y: 24
      });
      gsap.to(".richmond-header", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".richmond-card", {
        y: 32
      });
      gsap.to(".richmond-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.08,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".richmond-cards-grid",
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".safety-panel", {
        y: 32
      });
      gsap.to(".safety-panel", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".safety-panel",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".richmond-header", ".richmond-card", ".safety-panel"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="richmondaccessandsafety" data-nav="light" ref={containerRef} className="bg-background text-foreground py-20 md:py-28 overflow-hidden border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="richmond-header max-w-3xl mb-16" style={{
        opacity: 0
      }}>
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="text-foreground border-border bg-accent/40 font-medium">
              <MapPin className="size-3 text-primary" />
              Richmond & Surrey Access
            </Badge>
            <span className="text-xs text-muted-foreground font-mono">TG20:21 & NASC Standard</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground mb-6">
            Scaffolding engineered for Richmond's heritage and tight streets
          </h2>

          <p className="text-lg text-muted-foreground leading-relaxed">
            Working in Richmond demands exceptional care for conservation areas, historic brickwork, narrow access lanes, and active residential neighbourhoods. We build safe, compliant scaffolding structures tailored to these exact conditions.
          </p>
        </div>

        {/* Access Contexts Grid */}
        <div className="richmond-cards-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {accessContexts.map((context, i) => {
          const Icon = context.icon;
          return <Card key={context.title} data-index={i} className="richmond-card bg-card border-border hover:border-primary/50 transition-colors shadow-sm hover:shadow-md" style={{
            opacity: 0
          }}>
                <CardHeader className="pb-3">
                  <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-3">
                    <Icon className="size-5" />
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground font-default">
                    {context.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                    {context.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Safety, Compliance & Accreditations Banner */}
        <div className="safety-panel bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-12 shadow-md mb-16" style={{
        opacity: 0
      }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Left: Safety Workflow */}
            <div className="lg:col-span-7 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
                  <ShieldCheck className="size-4" />
                  Verified Safety System
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default tracking-tight mb-4">
                  Rigorous site safety from planning to strike
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-8">
                  Every project follows a structured health and safety management sequence. We never skip site surveys or handover checks, ensuring total protection for trades, homeowners, and the general public.
                </p>
              </div>

              {/* 4 Steps */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {safetyProtocol.map((item, index) => <div key={item.step} data-index={index} className="p-4 rounded-xl bg-accent/30 border border-border/70 flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono font-bold text-primary">{item.step}</span>
                      <CheckCircle2 className="size-4 text-primary" />
                    </div>
                    <span className="font-semibold text-foreground text-sm font-default">{item.name}</span>
                    <p className="text-xs text-muted-foreground leading-normal">{item.detail}</p>
                  </div>)}
              </div>
            </div>

            {/* Right: Accreditations & Guarantees */}
            <div className="lg:col-span-5 bg-accent/40 rounded-xl p-6 sm:p-8 border border-border flex flex-col justify-between h-full">
              <div>
                <h4 className="text-lg font-bold text-foreground font-default mb-2 flex items-center gap-2">
                  <FileCheck className="size-5 text-primary" />
                  Recognised safety accreditations
                </h4>
                <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
                  Our working practices and management processes are formally audited and accredited by leading industry safety bodies.
                </p>

                <div className="space-y-4 mb-8">
                  {/* NASC Badge Item */}
                  <div className="flex items-start gap-3 p-3.5 rounded-lg bg-card border border-border">
                    <div className="size-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0 text-primary font-bold text-xs font-mono">
                      NASC
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground font-default">
                        NASC Audited Contractor
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        National Access & Scaffolding Confederation compliant practices, technical guidance, and CISRS trained operatives.
                      </p>
                    </div>
                  </div>

                  {/* CHAS Badge Item */}
                  <div className="flex items-start gap-3 p-3.5 rounded-lg bg-card border border-border">
                    <div className="size-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0 text-primary font-bold text-xs font-mono">
                      CHAS
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground font-default">
                        CHAS Accredited
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Contractors Health and Safety Assessment Scheme verified standard for risk assessment and site health management.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Handover Inspection Note */}
              <div className="pt-4 border-t border-border/80">
                <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                  <ClipboardCheck className="size-4 text-primary" />
                  Full Handover Certificate & Scafftag with every build
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Call to Action Bar */}
        <div className="bg-muted/40 rounded-xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 border border-border">
          <div className="text-center md:text-left">
            <h4 className="text-lg font-bold text-foreground font-default mb-1">
              Need safe access for a Richmond project?
            </h4>
            <p className="text-sm text-muted-foreground">
              Speak with our estimators for site surveys, pavement licence advice, and detailed quotations.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="default" size="lg" className="rounded-md">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-md bg-card">
              <Link to="/HealthSafetyAccreditationsPage">
                <Shield className="size-4 mr-1.5" />
                View accreditations
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}